import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';

/**
 * Premium Founder Section Component
 * Executive-level design with authority, stability, trust, and professionalism
 */
const FounderSection = ({ variant = 'full' }) => {
  const founderImage = 'https://customer-assets.emergentagent.com/job_matax-platform/artifacts/ttwnagf6_image.png';
  
  const credentials = [
    'PTIN Holder',
    'CPE Certified',
    'All 50 States'
  ];

  // Compact version for homepage
  if (variant === 'compact') {
    return (
      <section className="py-20 lg:py-28 bg-white" data-testid="founder-section-compact">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image - Left Column */}
            <div className="relative animate-fadeInUp order-2 lg:order-1">
              <div className="relative">
                {/* Subtle background accent */}
                <div 
                  className="absolute -inset-4 rounded-3xl opacity-[0.03]"
                  style={{ background: '#0B1F3A' }}
                />
                
                {/* Main Image Container */}
                <div className="relative overflow-hidden rounded-2xl shadow-xl">
                  <img
                    src={founderImage}
                    alt="Fajlur Rahman - Founder & Lead Tax Preparer"
                    className="w-full h-[420px] lg:h-[500px] object-cover object-top"
                    style={{ 
                      objectPosition: 'center 15%',
                    }}
                  />
                  
                  {/* Subtle gradient overlay at bottom */}
                  <div 
                    className="absolute inset-x-0 bottom-0 h-24"
                    style={{ 
                      background: 'linear-gradient(to top, rgba(11,31,58,0.1) 0%, transparent 100%)' 
                    }}
                  />
                </div>

                {/* Floating credential badge */}
                <div className="absolute -bottom-4 left-6 right-6 bg-white rounded-xl p-4 shadow-lg border border-gray-100">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    {credentials.map((cred, i) => (
                      <div key={i} className="flex items-center gap-1.5">
                        <CheckCircle size={14} className="text-emerald-500 flex-shrink-0" />
                        <span className="text-xs font-medium text-gray-600">{cred}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Content - Right Column */}
            <div className="animate-fadeInUp animation-delay-200 order-1 lg:order-2">
              <div 
                className="inline-block px-3 py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase mb-6"
                style={{ background: '#0B1F3A08', color: '#0B1F3A' }}
              >
                Meet Our Founder
              </div>
              
              <h2 className="font-heading text-3xl lg:text-4xl font-bold text-[#0B1F3A] tracking-tight mb-2">
                Fajlur Rahman
              </h2>
              
              <p className="text-lg text-[#64748B] font-medium mb-6">
                PTP, CPE · Founder & Lead Tax Preparer
              </p>

              {/* Elegant divider */}
              <div className="w-16 h-0.5 bg-gradient-to-r from-[#D4AF37] to-[#D4AF37]/30 mb-6" />

              <p className="text-[#475569] leading-relaxed text-base mb-8">
                Fajlur Rahman is a results-driven leader with a strong commitment to integrity, compliance, and client success. With extensive experience in business operations and financial services, he leads the company with a vision focused on excellence, transparency, and long-term growth.
              </p>

              <Link to="/contact" data-testid="founder-connect-btn">
                <button 
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
                  style={{ 
                    background: '#0B1F3A', 
                    color: 'white' 
                  }}
                >
                  Connect With Us
                  <ArrowRight size={16} />
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Full version for About page
  return (
    <section className="py-20 lg:py-28 bg-white" data-testid="founder-section-full">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image - Left Column */}
          <div className="relative animate-fadeInUp">
            <div className="relative">
              {/* Subtle background accent */}
              <div 
                className="absolute -inset-6 rounded-3xl"
                style={{ background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)' }}
              />
              
              {/* Main Image Container */}
              <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                <img
                  src={founderImage}
                  alt="Fajlur Rahman - Founder & Lead Tax Preparer"
                  className="w-full h-[480px] lg:h-[580px] object-cover object-top"
                  style={{ 
                    objectPosition: 'center 15%',
                  }}
                />
                
                {/* Subtle gradient overlay */}
                <div 
                  className="absolute inset-x-0 bottom-0 h-32"
                  style={{ 
                    background: 'linear-gradient(to top, rgba(11,31,58,0.15) 0%, transparent 100%)' 
                  }}
                />
              </div>

              {/* Floating title card */}
              <div 
                className="absolute -bottom-6 left-6 right-6 rounded-xl p-5 shadow-xl border border-gray-100"
                style={{ background: 'white' }}
              >
                <div className="flex items-center gap-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: '#D4AF3720' }}
                  >
                    <svg className="w-6 h-6" style={{ color: '#D4AF37' }} fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="font-heading font-bold text-[#0B1F3A] text-lg">Fajlur Rahman</div>
                    <div className="text-[#64748B] text-sm">PTP, CPE · Founder & Lead Tax Preparer</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content - Right Column */}
          <div className="animate-fadeInUp animation-delay-200 lg:pl-4">
            <div 
              className="inline-block px-4 py-2 rounded-full text-xs font-semibold tracking-wide uppercase mb-6"
              style={{ background: '#2563EB10', color: '#2563EB' }}
            >
              Our Founder
            </div>
            
            <h2 className="font-heading text-4xl lg:text-5xl font-bold text-[#0B1F3A] tracking-tight mb-2">
              Fajlur Rahman
            </h2>
            
            <p className="text-xl text-[#64748B] font-medium mb-6">
              Professional Tax Preparer (PTP), CPE Certified
            </p>

            {/* Elegant divider */}
            <div className="w-20 h-1 bg-gradient-to-r from-[#D4AF37] to-[#D4AF37]/20 rounded-full mb-8" />

            <div className="space-y-5 mb-10">
              <p className="text-[#475569] leading-relaxed text-base">
                Fajlur Rahman is a results-driven leader with a strong commitment to integrity, compliance, and client success. With extensive experience in business operations and financial services, he leads the company with a vision focused on excellence, transparency, and long-term growth.
              </p>
              <p className="text-[#475569] leading-relaxed text-base">
                Founded in 2021, MaTaxPreparer LLC was built with a mission to democratize access to high-quality tax preparation — the kind typically reserved for wealthy individuals and large corporations. Based in Kennesaw, Georgia, we proudly serve clients in all 50 states.
              </p>
            </div>

            {/* Credentials Grid */}
            <div className="grid grid-cols-2 gap-3 mb-10">
              {[
                'PTIN Holder',
                'CPE Certified',
                'Data Security Compliant',
                'All 50 States Coverage'
              ].map((cred, i) => (
                <div key={i} className="flex items-center gap-2 p-3 rounded-lg bg-gray-50/80">
                  <CheckCircle size={16} className="text-emerald-500 flex-shrink-0" />
                  <span className="text-sm text-[#475569] font-medium">{cred}</span>
                </div>
              ))}
            </div>

            <Link to="/contact" data-testid="founder-connect-btn-full">
              <button 
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-base transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5"
                style={{ 
                  background: '#0B1F3A', 
                  color: 'white' 
                }}
              >
                Connect With Us
                <ArrowRight size={18} />
              </button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FounderSection;
